'use client';

import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import controls from './PublicSiteControls.module.css';

import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';

import type { SeoGuideHeroSlide } from '@/lib/seo/guides';

const SLIDE_INTERVAL_MS = 3000;

interface GuideArticleHeroSlideshowProps {
  slides: SeoGuideHeroSlide[];
  label: string;
  pauseLabel: string;
  resumeLabel: string;
}

export function GuideArticleHeroSlideshow({ slides, label, pauseLabel, resumeLabel }: GuideArticleHeroSlideshowProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setAutoPlayEnabled(!mediaQuery.matches);
    updateMotionPreference();
    mediaQuery.addEventListener('change', updateMotionPreference);
    return () => mediaQuery.removeEventListener('change', updateMotionPreference);
  }, []);

  useEffect(() => {
    if (slides.length < 2 || !autoPlayEnabled || userPaused) return;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [autoPlayEnabled, slides.length, userPaused]);

  return (
    <div className="relative isolate aspect-[4/5] overflow-hidden">
      {slides.map((slide, index) => (
        <img
          key={slide.src}
          src={slide.src}
          alt={slide.alt}
          aria-hidden={index === activeIndex ? undefined : true}
          width={slide.width}
          height={slide.height}
          className={`absolute inset-0 h-full w-full object-cover object-center ${index === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0'} transition-opacity duration-700 ease-out`}
        />
      ))}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-[#071820]/[0.28] to-transparent px-4 pb-4 pt-10">
        <div className="pointer-events-auto flex items-center gap-1.5" role="group" aria-label={label}>
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              aria-pressed={index === activeIndex}
              aria-label={`${index + 1} / ${slides.length}`}
              onClick={() => setActiveIndex(index)}
              className="grid h-11 w-8 place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9]"
            ><span aria-hidden className={`block h-1.5 rounded-full transition-all ${index === activeIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/55'}`} /></button>
          ))}
        </div>
        <LiquidGlass as="button" type="button" aria-label={userPaused ? resumeLabel : pauseLabel} aria-pressed={userPaused} onClick={() => setUserPaused((paused) => !paused)} className={`${controls.button} ${controls.secondary} ${controls.icon} ml-3 pointer-events-auto`}>
          {userPaused ? <Play size={16} aria-hidden /> : <Pause size={16} aria-hidden />}
        </LiquidGlass>
      </div>
    </div>
  );
}
